/**
 * sliceDepth.js — где срез лежит по глубине.
 *
 * Вынесено из viewportSync ради одного: это единственная часть связанной
 * прокрутки, которую можно проверить арифметикой, а не глазами. viewportSync
 * тянет за собой Cornerstone и браузер, и внутри него любая проверка
 * превращается в «запусти и посмотри». Здесь же — чистые числа: ни импортов,
 * ни DOM. Тест ловит ошибку до сборки.
 *
 * Что считаем. У среза DICOM есть точка в пространстве пациента
 * (ImagePositionPatient) и две оси плоскости (ImageOrientationPatient).
 * Нормаль к плоскости — их векторное произведение; глубина среза — проекция
 * его точки на эту нормаль. Дальше сравнение двух исследований сводится к
 * поиску ближайшего числа, а не к возне с координатами.
 */

/**
 * Нормаль к плоскости среза. null, если ориентация не пришла или вырождена
 * (такое бывает у вторичных захватов — там просто нет геометрии).
 * @param {number[]} iop — шесть чисел ImageOrientationPatient
 */
export function planeNormal(iop) {
  if (!Array.isArray(iop) || iop.length < 6) return null;
  const [rx, ry, rz, cx, cy, cz] = iop.map(Number);
  if (![rx, ry, rz, cx, cy, cz].every(Number.isFinite)) return null;
  const n = [ry * cz - rz * cy, rz * cx - rx * cz, rx * cy - ry * cx];
  const len = Math.hypot(n[0], n[1], n[2]);
  if (!len || !Number.isFinite(len)) return null;   // оси совпали — плоскости нет
  return [n[0] / len, n[1] / len, n[2] / len];
}

/**
 * Глубина точки вдоль нормали.
 * @param {number[]} ipp — три числа ImagePositionPatient
 * @param {number[]} n — единичная нормаль
 */
export function projectOnNormal(ipp, n) {
  if (!Array.isArray(ipp) || ipp.length < 3 || !Array.isArray(n) || n.length < 3) return null;
  const v = ipp[0] * n[0] + ipp[1] * n[1] + ipp[2] * n[2];
  return Number.isFinite(v) ? v : null;
}

/**
 * Монотонна ли последовательность глубин.
 *
 * Проверяем, а не верим. Срезы сортируются при загрузке (sortSlices), и в
 * обычной серии глубины идут ровно. Но серия может прийти склеенной из двух
 * наборов — например, при повторной съёмке того же уровня, — и тогда порядок
 * ломается. Поиск делением пополам на немонотонном ряду молча вернёт не тот
 * срез, а врач увидит не тот уровень и не узнает об этом.
 */
export function analyzeDepths(depths) {
  const n = depths?.length || 0;
  if (n < 2) return { monotonic: true, ascending: true };
  let ascending = true, descending = true;
  for (let i = 1; i < n; i++) {
    if (depths[i] < depths[i - 1]) ascending = false;
    if (depths[i] > depths[i - 1]) descending = false;
  }
  return { monotonic: ascending || descending, ascending };
}

/**
 * Номер среза, чья глубина ближе всего к заданной.
 *
 * На монотонном ряду — делением пополам: пять тысяч срезов при каждом
 * повороте колеса перебирать нельзя, прокрутка станет рывками. На
 * немонотонном — честный перебор: медленнее, зато правильно.
 *
 * @param {number[]} depths
 * @param {number} target
 * @param {{monotonic?: boolean, ascending?: boolean}} [order] — результат analyzeDepths
 */
export function nearestIndex(depths, target, order) {
  const n = depths?.length || 0;
  if (!n) return 0;
  if (!Number.isFinite(target)) return 0;

  const { monotonic = false, ascending = true } = order || analyzeDepths(depths);

  if (!monotonic) {
    let лучший = 0, дельта = Infinity;
    for (let i = 0; i < n; i++) {
      const d = Math.abs(depths[i] - target);
      if (d < дельта) { дельта = d; лучший = i; }
    }
    return лучший;
  }

  let lo = 0, hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const меньше = ascending ? depths[mid] < target : depths[mid] > target;
    if (меньше) lo = mid + 1; else hi = mid;
  }
  // Деление пополам останавливается на первом «не меньше» — сосед слева может
  // оказаться ближе. Без этой проверки прокрутка систематически уезжает на
  // полсреза вперёд, и на тонких срезах это заметно.
  if (lo > 0 && Math.abs(depths[lo - 1] - target) <= Math.abs(depths[lo] - target)) return lo - 1;
  return lo;
}
